import { useEffect, useState } from "react";
import { getData } from "@/Action/api";
import { getAllGallery } from "@/Action/apirouts";
import { ASSETS_URL } from "@/Constant";

const LIMIT = 12;

const Gallery = () => {
  const [galleryData, setGalleryData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [lightbox, setLightbox] = useState(null); // selected item for popup

  const fetchGallery = async (currentPage = 1) => {
    setLoading(true);
    try {
      const result = await getData(
        `${getAllGallery}?page=${currentPage}&limit=${LIMIT}`
      );
      if (result?.success) {
        if (currentPage === 1) {
          setGalleryData(result.data);
        } else {
          setGalleryData((prev) => [...prev, ...result.data]);
        }
        setTotalPages(result.totalPages || 1);
      }
    } catch (error) {
      console.error("Error fetching gallery:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchGallery(1);
  }, []);

  const goToOffset = (offset) => {
    if (!lightbox) return;
    const idx = galleryData.findIndex((g) => g.id === lightbox.id);
    if (idx === -1) return;
    const nextIdx = (idx + offset + galleryData.length) % galleryData.length;
    setLightbox(galleryData[nextIdx]);
  };

  // Keyboard: Esc closes, arrows navigate
  useEffect(() => {
    const handleKey = (e) => {
      if (!lightbox) return;
      if (e.key === "Escape") setLightbox(null);
      if (e.key === "ArrowRight") goToOffset(1);
      if (e.key === "ArrowLeft") goToOffset(-1);
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [lightbox, galleryData]);

  // Prevent body scroll when lightbox open
  useEffect(() => {
    if (lightbox) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => { document.body.style.overflow = ""; };
  }, [lightbox]);

  const handleLoadMore = () => {
    const nextPage = page + 1;
    setPage(nextPage);
    fetchGallery(nextPage);
  };

  const imgUrl = (item) =>
    `${ASSETS_URL}/uploads/gallery/${item.id}/${item.image?.src}`;

  return (
    <div className="rbt-section-gap">
      <div className="container">

        {/* Section Heading */}
        <div className="row">
          <div className="col-12">
            <div className="section-title text-center mb-5">
              <span
                style={{
                  color: "#DB6827",
                  fontWeight: "600",
                  fontSize: "15px",
                  letterSpacing: "1px",
                  textTransform: "uppercase",
                  display: "block",
                  marginBottom: "10px",
                }}
              >
                Our Moments
              </span>
              <h2 className="title mt-2 mb-3">
                Our <span style={{ color: "#DB6827" }}>Gallery</span>
              </h2>
              <p
                style={{
                  color: "#6B7280",
                  fontSize: "16px",
                  maxWidth: "580px",
                  margin: "0 auto",
                  lineHeight: "1.7",
                }}
              >
                Explore our collection of captured memories — from campus visits
                and study abroad experiences to events and milestones that define
                the journey of our students worldwide.
              </p>
            </div>
          </div>
        </div>

        {/* Gallery Grid */}
        {loading && galleryData.length === 0 ? (
          <div
            style={{
              minHeight: "400px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        ) : galleryData.length > 0 ? (
          <>
            <div className="gallery-masonry">
              {galleryData.map((item) => (
                <div
                  key={item.id}
                  className="gallery-card"
                  onClick={() => setLightbox(item)}
                  style={{
                    position: "relative",
                    borderRadius: "12px",
                    overflow: "hidden",
                    boxShadow: "0 4px 20px rgba(0,0,0,0.10)",
                    cursor: "pointer",
                    breakInside: "avoid",
                    marginBottom: "24px",
                    display: "block",
                  }}
                >
                  {/* Image — natural aspect ratio */}
                  <img
                    src={imgUrl(item)}
                    alt={item.altText || item.title}
                    className="gallery-img"
                    style={{
                      width: "100%",
                      height: "auto",
                      display: "block",
                      transition: "transform 0.4s ease",
                    }}
                  />

                  {/* Hover Overlay */}
                  <div
                    className="gallery-overlay"
                    style={{
                      position: "absolute",
                      inset: 0,
                      background:
                        "linear-gradient(to top, rgba(0,0,0,0.75) 0%, rgba(0,0,0,0.15) 55%, transparent 100%)",
                      opacity: 0,
                      transition: "opacity 0.35s ease",
                      display: "flex",
                      alignItems: "flex-end",
                      padding: "20px",
                    }}
                  >
                      <div style={{ flex: 1 }}>
                        {item.title && (
                          <h6
                            style={{
                              color: "#fff",
                              fontWeight: "600",
                              marginBottom: "4px",
                              fontSize: "15px",
                            }}
                          >
                            {item.title}
                          </h6>
                        )}
                        {item.description && (
                          <p
                            style={{
                              color: "rgba(255,255,255,0.82)",
                              fontSize: "13px",
                              marginBottom: 0,
                              lineHeight: "1.5",
                            }}
                          >
                            {item.description}
                          </p>
                        )}
                      </div>
                      {/* Expand icon */}
                      <div
                        style={{
                          width: "36px",
                          height: "36px",
                          borderRadius: "50%",
                          background: "rgba(255,255,255,0.2)",
                          border: "1px solid rgba(255,255,255,0.4)",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          flexShrink: 0,
                          marginLeft: "10px",
                        }}
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                          <polyline points="15 3 21 3 21 9" />
                          <polyline points="9 21 3 21 3 15" />
                          <line x1="21" y1="3" x2="14" y2="10" />
                          <line x1="3" y1="21" x2="10" y2="14" />
                        </svg>
                      </div>
                    </div>
                  </div>
              ))}
            </div>

            {/* Load More */}
            {page < totalPages && (
              <div className="row mt-5">
                <div className="col-12 text-center">
                  <button
                    className="rbt-btn btn-gradient radius-round"
                    onClick={handleLoadMore}
                    disabled={loading}
                    style={{ minWidth: "160px" }}
                  >
                    {loading ? "Loading..." : "Load More"}
                  </button>
                </div>
              </div>
            )}
          </>
        ) : (
          <div
            style={{
              minHeight: "300px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <p style={{ color: "#4D4D4D", fontWeight: "500", fontSize: "20px" }}>
              No gallery images found.
            </p>
          </div>
        )}
      </div>

      {/* Masonry + hover styles */}
      <style jsx>{`
        .gallery-masonry {
          column-count: 3;
          column-gap: 24px;
        }
        @media (max-width: 991px) {
          .gallery-masonry { column-count: 2; }
        }
        @media (max-width: 575px) {
          .gallery-masonry { column-count: 1; }
        }
        .gallery-card:hover :global(.gallery-overlay) {
          opacity: 1;
        }
        .gallery-card:hover :global(.gallery-img) {
          transform: scale(1.06);
        }
      `}</style>

      {/* Lightbox */}
      {lightbox && (
        <div
          onClick={() => setLightbox(null)}
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 999999,
            backgroundColor: "rgba(0,0,0,0.88)",
            backdropFilter: "blur(6px)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "20px",
            animation: "lbFadeIn 0.25s ease",
          }}
        >
          {/* Close button */}
          <button
            onClick={() => setLightbox(null)}
            style={{
              position: "absolute",
              top: "20px",
              right: "24px",
              background: "rgba(255,255,255,0.12)",
              border: "1px solid rgba(255,255,255,0.25)",
              color: "#fff",
              width: "42px",
              height: "42px",
              borderRadius: "50%",
              fontSize: "20px",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 10,
              transition: "background 0.15s",
            }}
            aria-label="Close"
          >
            &#x2715;
          </button>

          {/* Prev button */}
          {galleryData.length > 1 && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                goToOffset(-1);
              }}
              className="lb-nav-btn"
              style={{
                position: "absolute",
                left: "24px",
                top: "50%",
                transform: "translateY(-50%)",
                background: "rgba(255,255,255,0.12)",
                border: "1px solid rgba(255,255,255,0.25)",
                color: "#fff",
                width: "48px",
                height: "48px",
                borderRadius: "50%",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                zIndex: 10,
                transition: "background 0.15s",
              }}
              aria-label="Previous image"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="15 18 9 12 15 6" />
              </svg>
            </button>
          )}

          {/* Next button */}
          {galleryData.length > 1 && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                goToOffset(1);
              }}
              className="lb-nav-btn"
              style={{
                position: "absolute",
                right: "24px",
                top: "50%",
                transform: "translateY(-50%)",
                background: "rgba(255,255,255,0.12)",
                border: "1px solid rgba(255,255,255,0.25)",
                color: "#fff",
                width: "48px",
                height: "48px",
                borderRadius: "50%",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                zIndex: 10,
                transition: "background 0.15s",
              }}
              aria-label="Next image"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="9 18 15 12 9 6" />
              </svg>
            </button>
          )}

          {/* Image box */}
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: "#fff",
              borderRadius: "16px",
              overflow: "hidden",
              maxWidth: "min(820px, 95vw)",
              maxHeight: "92vh",
              width: "auto",
              boxShadow: "0 30px 80px rgba(0,0,0,0.5)",
              animation: "lbSlideUp 0.28s ease",
              display: "flex",
              flexDirection: "column",
            }}
          >
            {/* Image — natural aspect ratio */}
            <img
              src={imgUrl(lightbox)}
              alt={lightbox.altText || lightbox.title}
              style={{
                display: "block",
                maxWidth: "100%",
                maxHeight: "75vh",
                width: "auto",
                height: "auto",
                objectFit: "contain",
              }}
            />


            {/* Caption */}
            {(lightbox.title || lightbox.description) && (
              <div style={{ padding: "20px 24px" }}>
                {lightbox.title && (
                  <h5 style={{ fontWeight: "700", color: "#0f172a", marginBottom: "6px" }}>
                    {lightbox.title}
                  </h5>
                )}
                {lightbox.description && (
                  <p style={{ color: "#64748b", fontSize: "14px", marginBottom: 0, lineHeight: "1.6" }}>
                    {lightbox.description}
                  </p>
                )}
              </div>
            )}
          </div>

          <style>{`
            @keyframes lbFadeIn {
              from { opacity: 0; }
              to   { opacity: 1; }
            }
            @keyframes lbSlideUp {
              from { opacity: 0; transform: translateY(24px); }
              to   { opacity: 1; transform: translateY(0); }
            }
          `}</style>
        </div>
      )}
    </div>
  );
};

export default Gallery;
